import * as support from "./LossCurves.support";
import { SettingsBackupRestore as ResetIcon } from "@mui/icons-material";
import { Box, Stack, IconButton } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import Component from "components/Component";
import CopyButton from "components/common/CopyButton";
import Disabled from "components/common/Disabled";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
} from "react";

const PasteManager = ({ children, onPaste, disabled = false }) => {
  const wrapperRef = useRef(null);
  const lastClickRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (wrapper) {
      const handlePasteEvent = (event) => {
        event.preventDefault();
        if (onPaste && event.clipboardData && !disabled) {
          onPaste({
            lastClick: lastClickRef.current,
            pastedText: event.clipboardData.getData("text/plain"),
          });
        }
      };
      wrapper.addEventListener("paste", handlePasteEvent);
      return () => {
        wrapper.removeEventListener("paste", handlePasteEvent);
      };
    }
  }, [wrapperRef, lastClickRef, disabled, onPaste]);

  return (
    <Box ref={wrapperRef}>
      {Children.map(children, (child) =>
        isValidElement(child)
          ? cloneElement(child, {
              slotProps: {
                cell: {
                  onFocus: (event) => {
                    lastClickRef.current = {
                      rowId: event.currentTarget.parentElement.dataset.id,
                      columnId: event.currentTarget.dataset.field,
                    };
                  },
                },
              },
            })
          : child
      )}
    </Box>
  );
};

const returnPeriods = [
  50000,
  10000,
  5000,
  1000,
  500,
  250,
  200,
  100,
  50,
  25,
  10,
  5,
  2,
];

const defaultEP = returnPeriods.map((returnPeriod, index) => {
  return { id: index, returnPeriod, grossLossAep: null, grossLossOep: null };
});

const ExceedenceProbabilities = ({
  modelDataFacet,
  updateModelDataFacet,
  grossLossAepHeader,
  grossLossOepHeader,
  disabled = false,
}) => {
  const apiRef = useGridApiRef();

  const columns = [
    makePrettyNumberColDef({
      field: "returnPeriod",
      headerName: "Return Period",
      editable: false,
      minWidth: 120,
    }),
    makePrettyNumberColDef({
      field: "grossLossAep",
      headerName: grossLossAepHeader ?? "Gross Loss AEP",
      editable: true,
      minWidth: 220,
    }),
    makePrettyNumberColDef({
      field: "grossLossOep",
      headerName: grossLossOepHeader ?? "Gross Loss OEP",
      editable: true,
      minWidth: 220,
    }),
  ].map((column) => (disabled ? { ...column, editable: false } : column));

  const ep = modelDataFacet?.exceedenceProbabilities ?? defaultEP;

  const processRowUpdate = (newRow) => {
    const updatedData = ep?.map((row) => (row.id === newRow.id ? newRow : row));
    updateModelDataFacet({ exceedenceProbabilities: updatedData });
    return newRow;
  };

  return (
    <PasteManager
      onPaste={({ lastClick, pastedText }) => {
        if (lastClick?.rowId == null || lastClick?.columnId == null) {
          return;
        }
        if (lastClick.columnId === "returnPeriod") {
          return;
        }

        const newExceedenceProbabilities = support.applyPastedValues(
          ep,
          pastedText,
          lastClick
        );

        updateModelDataFacet({
          exceedenceProbabilities: newExceedenceProbabilities,
        });
      }}
      disabled={disabled}
    >
      <DataGrid
        apiRef={apiRef}
        columns={columns}
        rows={ep}
        rowHeight={25}
        getCellClassName={(_) => "tabular"}
        processRowUpdate={processRowUpdate}
        onCellEditStart={(params, event) => {
          if (params?.reason === "deleteKeyDown") {
            updateModelDataFacet({
              exceedenceProbabilities: modelDataFacet?.exceedenceProbabilities?.map(
                (ep) =>
                  ep.id === params.id
                    ? {
                        ...ep,
                        [params.field]: null,
                      }
                    : ep
              ),
            });
            event.defaultMuiPrevented = true;
            return;
          } else if (params?.reason === "pasteKeyDown") {
            event.defaultMuiPrevented = true;
            return;
          }
        }}
        hideFooter
        disableColumnMenu
        autoHeight
      />
    </PasteManager>
  );
};

const SummaryStats = ({
  modelDataFacet,
  updateModelDataFacet,
  disabled = false,
}) => {
  const apiRef = useGridApiRef();

  const columns = [
    {
      field: "label",
      headerName: "Metric",
      type: "text",
      editable: false,
      width: 220,
    },
    makePrettyNumberColDef({
      field: "metric",
      headerName: "Value",
      width: 120,
    }),
  ].map((column) => (disabled ? { ...column, editable: false } : column));

  const rows = [
    {
      id: "expectedLoss",
      label: "Expected Value",
      metric: modelDataFacet?.summaryStats?.expectedLoss ?? null,
    },
    {
      id: "standardDeviation",
      label: "Standard Deviation",
      metric: modelDataFacet?.summaryStats?.standardDeviation ?? null,
    },
    {
      id: "coefficientOfVariation",
      label: "Coefficient of Variation",
      metric: modelDataFacet?.summaryStats?.coefficientOfVariation ?? null,
    },
  ];

  return (
    <PasteManager
      onPaste={({ lastClick, pastedText }) => {
        if (lastClick?.rowId == null || lastClick?.columnId == null) {
          return;
        }
        if (lastClick.columnId !== "metric") {
          return;
        }

        const parseNumberGrid = (input) => {
          const floatTable = support.parseFloatTable(input);
          if (lastClick.rowId === "standardDeviation") {
            floatTable.unshift(null);
          } else if (lastClick.rowId === "coefficientOfVariation") {
            floatTable.unshift(null, null);
          }

          return {
            expectedLoss: floatTable?.[0]?.[0],
            standardDeviation: floatTable?.[1]?.[0],
            coefficientOfVariation: floatTable?.[2]?.[0],
          };
        };

        const updates = Object.fromEntries(
          Object.entries(
            support.parseRmsAal(pastedText) ?? parseNumberGrid(pastedText) ?? {}
          ).filter(([_, value]) => value != null)
        );

        if (updates) {
          updateModelDataFacet({
            summaryStats: {
              ...modelDataFacet?.summaryStats,
              ...updates,
            },
          });
        }
      }}
      disabled={disabled}
    >
      <DataGrid
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        rowHeight={25}
        getCellClassName={(_) => "tabular"}
        processRowUpdate={(newRow) => {
          updateModelDataFacet({
            summaryStats: {
              ...modelDataFacet?.summaryStats,
              [newRow.id]: newRow.metric,
            },
          });
          return newRow;
        }}
        onCellEditStart={(params, event) => {
          if (params?.reason === "deleteKeyDown") {
            updateModelDataFacet({
              summaryStats: {
                ...modelDataFacet?.summaryStats,
                [params.id]: null,
              },
            });
            event.defaultMuiPrevented = true;
            return;
          } else if (params?.reason === "pasteKeyDown") {
            event.defaultMuiPrevented = true;
            return;
          }
        }}
        hideFooter
        disableColumnMenu
        autoHeight
      />
    </PasteManager>
  );
};

const LossCurves = ({
  label,
  modelDataFacet,
  updateModelDataFacet,
  grossLossAepHeader,
  grossLossOepHeader,
}) => {
  const copyDataContent = (modelDataFacet?.exceedenceProbabilities ?? defaultEP)
    .map((x) => `${x.grossLossAep ?? ""}\t${x.grossLossOep ?? ""}`)
    .join("\n");

  const copyAllContent = [
    "Return Period\tGross Loss AEP\tGross Loss OEP",
    ...(modelDataFacet?.exceedenceProbabilities ?? defaultEP).map(
      (x) =>
        `${x.returnPeriod}\t${x.grossLossAep ?? ""}\t${x.grossLossOep ?? ""}`
    ),
  ].join("\n");

  return (
    <Component
      title={label}
      options={
        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
          <CopyButton
            content={copyDataContent}
            label={"Copy data"}
            variant={"button"}
            scale={"small"}
          />
          <CopyButton
            content={copyAllContent}
            label={"Copy all"}
            variant={"button"}
            scale={"small"}
          />
          <IconButton
            color={"primary"}
            onClick={() =>
              updateModelDataFacet({
                exceedenceProbabilities: null,
                summaryStats: null,
              })
            }
            tooltip={"Clear"}
          >
            <ResetIcon />
          </IconButton>
        </Stack>
      }
    >
      <Stack direction={"column"} spacing={2}>
        <Disabled ifReadOnly>
          <ExceedenceProbabilities
            modelDataFacet={modelDataFacet}
            updateModelDataFacet={updateModelDataFacet}
            grossLossAepHeader={grossLossAepHeader}
            grossLossOepHeader={grossLossOepHeader}
          />
        </Disabled>
        <Disabled ifReadOnly>
          <SummaryStats
            modelDataFacet={modelDataFacet}
            updateModelDataFacet={updateModelDataFacet}
          />
        </Disabled>
      </Stack>
    </Component>
  );
};

export default LossCurves;
